import React from 'react';

import {
  Avatar,
  Box,
  Button,
  FormControl,
  Grid,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Stack,
  SvgIcon,
  TextField,
} from '@mui/material';
import {useForm} from 'react-hook-form';
import {defineMessages, useIntl} from 'react-intl';
import {useNavigate} from 'react-router-dom';
import {useMutation, useQuery, useQueryClient} from 'react-query';

import API, {
  Types,
  VehicleGetProps,
  VehicleCreateRequest,
  VehicleUpdateRequest,
  useCsrfToken,
} from '@wheeebo/polaris-api';
import {useSnackbar} from '@wheeebo/polaris-snackbar';
import {DroneIcon, HoverbikeIcon} from '@wheeebo/polaris-svg';
import {CircleLoader, LoadError, ValidatableField, useErrorHandler} from '@wheeebo/polaris-ui';

const msgs = defineMessages({
  errorNoVehicle: {
    id: 'form.VehicleEditForm.error.noVehicle',
    defaultMessage: 'vehicle data not found',
  },
  buttonCancel: {
    id: 'form.VehicleEditForm.button.cancel',
    defaultMessage: 'Cancel',
  },
  buttonSubmit: {
    id: 'form.VehicleEditForm.button.submit',
    defaultMessage: 'Submit',
  },
  inputNameLabel: {
    id: 'form.VehicleEditForm.input.nameLabel',
    defaultMessage: 'Name',
  },
  inputNamePlaceholder: {
    id: 'form.VehicleEditForm.input.namePlaceholder',
    defaultMessage: 'Vehicle name',
  },
  inputNameHelperText1: {
    id: 'form.VehicleEditForm.input.nameHelperText1',
    defaultMessage: 'Enter the vehicle name, which can give you hints for identifying your vehicle.',
  },
  inputSerialNumberLabel: {
    id: 'form.VehicleEditForm.input.serialNumberLabel',
    defaultMessage: 'Serial Number',
  },
  inputSerialNumberPlaceholder: {
    id: 'form.VehicleEditForm.input.serialNumberlaceholder',
    defaultMessage: 'Serial number of your vehicle',
  },
  inputSerialNumberHelperText: {
    id: 'form.VehicleEditForm.input.serialNumberHelperText',
    defaultMessage: 'Enter serial number of your vehicle.',
  },
  inputEmailLabel: {
    id: 'form.VehicleEditForm.input.emailLabel',
    defaultMessage: "Owner's Email",
  },
  inputEmailPlaceholder: {
    id: 'form.VehicleEditForm.input.emailPlaceholder',
    defaultMessage: 'abc@example.com',
  },
  inputEmailHelperText1: {
    id: 'form.VehicleEditForm.input.emailHelperText1',
    defaultMessage: 'Enter the email of owner of this vehicle. The email should already be registered in polaris',
  },
  successVehicleCreated: {
    id: 'form.VehicleEditForm.success.vehicleCreated',
    defaultMessage: 'Vehicle created',
  },
  successVehicleUpdated: {
    id: 'form.VehicleEditForm.success.accountUpdated',
    defaultMessage: 'Vehicle updated',
  },
});

interface Props {
  vehicleId?: string;
  locked?: boolean;
  showFormSubmitButton?: boolean;
}

function VehicleEditForm({vehicleId: vehicleId, locked = false, showFormSubmitButton = false}: Props): JSX.Element {
  const intl = useIntl();
  const {csrfToken} = useCsrfToken();
  const navigate = useNavigate();
  const {register, formState, handleSubmit: validate} = useForm({mode: 'onChange'});
  const {errors} = formState;
  const {showAndEmitJsErrorEvent, showError} = useErrorHandler();
  const {showSuccess} = useSnackbar();
  const queryClient = useQueryClient();
  const [vehicleType, setVehicleType] = React.useState<Types.VehicleType>(Types.VehicleTypeDrone);
  const [name, setName] = React.useState<string>();
  const [serialNumber, setSerialNumber] = React.useState<string>();
  const [email, setEmail] = React.useState<string>();
  const options: VehicleGetProps = {preload: ['owner']};
  const {data, isError, isLoading} = useQuery(
    vehicleId ? API.Vehicles.getKey(vehicleId, options) : '',
    () => (vehicleId ? API.Vehicles.get(vehicleId, options) : undefined),
    {enabled: !!vehicleId},
  );
  const {mutate: createVehicle} = useMutation<Types.Vehicle, unknown, VehicleCreateRequest>(
    (params: VehicleCreateRequest) => API.Vehicles.create(csrfToken, params),
    {
      onSuccess: async (vehicle: Types.Vehicle) => {
        queryClient.invalidateQueries(API.Vehicles.CacheKey);
        showSuccess(intl.formatMessage(msgs.successVehicleCreated));
        navigate(`/vehicles/${vehicle.id}`);
      },
      onError: showAndEmitJsErrorEvent,
    },
  );
  const handleSubmit = () => {
    if (
      !vehicleType ||
      !name ||
      !serialNumber ||
      !email ||
      errors['vehicle_type'] ||
      errors['name'] ||
      errors['serialNumber'] ||
      errors['email']
    ) {
      return;
    }
    createVehicle({vehicleType, name, serialNumber, email});
  };
  const {mutate: updateVehicle} = useMutation<unknown, unknown, VehicleUpdateRequest>(
    (props: VehicleUpdateRequest) => {
      if (!data) {
        showError(new Error(intl.formatMessage(msgs.errorNoVehicle)));
        return Promise.reject({});
      }
      return API.Vehicles.update(csrfToken, data.id, props);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(API.Vehicles.CacheKey);
        showSuccess(intl.formatMessage(msgs.successVehicleUpdated));
        navigate('.', {replace: true});
      },
      onError: showAndEmitJsErrorEvent,
    },
  );
  const saveVehicleType = (newValue: string) => {
    if (!errors['vehicle_type']) {
      updateVehicle({vehicleType: newValue});
      return true;
    }
    return false;
  };
  const saveName = (newValue: string) => {
    if (!errors['name']) {
      updateVehicle({name: newValue});
      return true;
    }
    return false;
  };
  const saveSerialNumber = (newValue: string) => {
    if (!errors['serialNumber']) {
      updateVehicle({serialNumber: newValue});
      return true;
    }
    return false;
  };
  const saveEmail = (newValue: string) => {
    if (!errors['email']) {
      updateVehicle({email: newValue});
      return true;
    }
    return false;
  };

  React.useEffect(() => setVehicleType(data?.vehicleType || Types.VehicleTypeDrone), [data?.vehicleType]);

  if (isLoading) {
    return <CircleLoader />;
  } else if (isError) {
    return <LoadError />;
  }
  return (
    <form onSubmit={validate(handleSubmit)}>
      <Grid container>
        <Grid item xs={12}>
          <Stack gap={1} sx={{flex: 1}}>
            <TextField disabled fullWidth variant="standard" label="Vehicle ID" value={vehicleId} />
            <FormControl component="fieldset" fullWidth>
              <ValidatableField
                select
                id="vehicle_type"
                label="Vehicle type"
                placeholder="Drone or Hoverbike"
                inputProps={{
                  sx: {display: 'flex', alignItems: 'center'},
                }}
                helperTexts={['Choose drone or hoverbike']}
                value={vehicleType || Types.VehicleTypeDrone}
                defaultValue={data?.vehicleType || Types.VehicleTypeDrone}
                registerOptions={{
                  value: data?.vehicleType || Types.VehicleTypeDrone,
                  required: true,
                  onChange: (e) => setVehicleType(e.target.value),
                }}
                onSave={saveVehicleType}
                editable={!showFormSubmitButton}
                {...{register, errors, locked}}>
                <MenuItem value={Types.VehicleTypeDrone}>
                  <ListItemAvatar>
                    <Avatar variant="rounded" sx={{width: 28, height: 28, backgroundColor: 'transparent'}}>
                      <SvgIcon fontSize="small" color="action">
                        <DroneIcon />
                      </SvgIcon>
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={Types.VehicleTypeDrone} />
                </MenuItem>
                <MenuItem value={Types.VehicleTypeHoverbike}>
                  <ListItemAvatar>
                    <Avatar variant="rounded" sx={{width: 28, height: 28, backgroundColor: 'transparent'}}>
                      <SvgIcon fontSize="small" color="action">
                        <HoverbikeIcon />
                      </SvgIcon>
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={Types.VehicleTypeHoverbike} />
                </MenuItem>
              </ValidatableField>
            </FormControl>
            <FormControl component="fieldset" fullWidth>
              <ValidatableField
                id="name"
                label={intl.formatMessage(msgs.inputNameLabel)}
                placeholder={intl.formatMessage(msgs.inputNamePlaceholder)}
                helperTexts={[intl.formatMessage(msgs.inputNameHelperText1)]}
                value={name}
                registerOptions={{
                  value: data?.name,
                  required: true,
                  maxLength: 255,
                  onChange: (e) => setName(e.target.value),
                }}
                onSave={saveName}
                editable={!showFormSubmitButton}
                {...{register, errors, locked}}
              />
            </FormControl>
            <FormControl component="fieldset" fullWidth>
              <ValidatableField
                id="serialNumber"
                label={intl.formatMessage(msgs.inputSerialNumberLabel)}
                placeholder={intl.formatMessage(msgs.inputSerialNumberPlaceholder)}
                helperTexts={[intl.formatMessage(msgs.inputSerialNumberHelperText)]}
                value={serialNumber}
                registerOptions={{
                  value: data?.serialNumber,
                  required: true,
                  maxLength: 255,
                  onChange: (e) => setSerialNumber(e.target.value),
                }}
                onSave={saveSerialNumber}
                editable={!showFormSubmitButton}
                {...{register, errors, locked}}
              />
            </FormControl>
            <FormControl component="fieldset" fullWidth>
              <ValidatableField
                id="email"
                label={intl.formatMessage(msgs.inputEmailLabel)}
                placeholder={intl.formatMessage(msgs.inputEmailPlaceholder)}
                helperTexts={[intl.formatMessage(msgs.inputEmailHelperText1)]}
                value={email}
                registerOptions={{
                  value: data?.owner?.email,
                  required: true,
                  maxLength: 255,
                  onChange: (e) => setEmail(e.target.value),
                }}
                onSave={saveEmail}
                editable={!showFormSubmitButton}
                {...{register, errors, locked}}
              />
            </FormControl>
            {showFormSubmitButton ? (
              <Box sx={{display: 'flex', mt: 5, flexDirection: 'row', justifyContent: 'space-evenly'}}>
                <Button variant="outlined" onClick={() => navigate('/vehicles')}>
                  {intl.formatMessage(msgs.buttonCancel)}
                </Button>
                <Button type="submit">{intl.formatMessage(msgs.buttonSubmit)}</Button>
              </Box>
            ) : null}
          </Stack>
        </Grid>
      </Grid>
    </form>
  );
}

export default React.memo(VehicleEditForm);
